// format.ts — the locale-aware number/date painter (ADR-030).
//
// The ONE place `Intl` is configured. Everything user-facing that renders a
// number or date goes through here (directly, or via formatValue.ts for
// physical quantities) so locale rules live in exactly one spot.
//
// Locked policy (ADR-030):
//   - Formatting locale is RESOLVED from the stored language, never stored
//     freeform: en→en-GB, fr→fr-FR, ar→ar-SA (day-first + metric across all).
//   - Explicit `Intl` options ALWAYS — never trust a locale's defaults. In
//     particular calendar:'gregory' is forced so ar-SA can never render Hijri.
//   - Digits follow the `numerals` preference: 'auto' → Arabic-Indic for ar,
//     Western otherwise; 'latin' → Western everywhere. Technical values
//     (phone/IDs/keys) pass { numerals: 'latin' } to force Western regardless.
//   - SI unit SYMBOLS stay Latin (kW, MWh) — they are locale-invariant; only
//     the number localizes. Unit symbols are appended by formatValue.ts.
//
// Performance: constructing an `Intl` formatter is ~100× the cost of calling
// one, so instances are CACHED by (locale + options). Callers never construct
// formatters; these helpers run on the majority of table/report/KPI cells.

import type { Language } from '@ems-app/schemas/common/language';
import type { Numerals } from '@ems-app/schemas/common/numerals';

// ── locale resolution ────────────────────────────────────────────────────

// Language → BCP-47 formatting locale. en-GB (not en-US) so English reads
// day-first + metric, consistent with fr and ar (ADR-030).
const LOCALE_BY_LANGUAGE: Record<Language, string> = {
  en: 'en-GB',
  fr: 'fr-FR',
  ar: 'ar-SA',
};

/**
 * Resolve the BCP-47 locale + numbering system from a language + numerals
 * preference. Pure — the single source of the digit policy. `'auto'` →
 * Arabic-Indic for Arabic, Western otherwise; `'latin'` forces Western.
 */
export function resolveLocale(
  language: Language,
  numerals: Numerals,
): { locale: string; numberingSystem: 'latn' | 'arab' } {
  const numberingSystem: 'latn' | 'arab' =
    numerals === 'latin' ? 'latn' : language === 'ar' ? 'arab' : 'latn';
  return { locale: LOCALE_BY_LANGUAGE[language], numberingSystem };
}

// ── ambient locale (server-authoritative, set from /me) ────────────────────

// The current render locale. Defaults to en/auto; the locale-sync hook calls
// setFormatLocale() from me.user.{language,numerals} (and on language change).
// Kept here — not read from i18n — so this module is pure + unit-testable
// without booting i18next.
let ambient: { language: Language; numerals: Numerals } = { language: 'en', numerals: 'auto' };

export function setFormatLocale(language: Language, numerals: Numerals): void {
  ambient = { language, numerals };
}

export function getFormatLocale(): { language: Language; numerals: Numerals } {
  return ambient;
}

// ── formatter caches ───────────────────────────────────────────────────────

const numberFormatters = new Map<string, Intl.NumberFormat>();
const dateFormatters = new Map<string, Intl.DateTimeFormat>();

function numberFormatter(locale: string, opts: Intl.NumberFormatOptions): Intl.NumberFormat {
  const key = `${locale}|${JSON.stringify(opts)}`;
  let fmt = numberFormatters.get(key);
  if (!fmt) {
    fmt = new Intl.NumberFormat(locale, opts);
    numberFormatters.set(key, fmt);
  }
  return fmt;
}

function dateFormatter(locale: string, opts: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const key = `${locale}|${JSON.stringify(opts)}`;
  let fmt = dateFormatters.get(key);
  if (!fmt) {
    fmt = new Intl.DateTimeFormat(locale, opts);
    dateFormatters.set(key, fmt);
  }
  return fmt;
}

// ── number formatting ──────────────────────────────────────────────────────

export interface NumberFormatOptions {
  /** Max fraction digits (default 2). Trailing zeros are stripped (min 0). */
  maximumFractionDigits?: number;
  /** Min fraction digits (default 0 — strip trailing zeros). */
  minimumFractionDigits?: number;
  /** Thousands grouping (default true; pass false for coordinates etc.). */
  useGrouping?: boolean;
  /** Force a digit system for this call (technical values → 'latin'). */
  numerals?: Numerals;
}

function intlNumberOptions(o: NumberFormatOptions, numberingSystem: 'latn' | 'arab'): Intl.NumberFormatOptions {
  return {
    numberingSystem,
    maximumFractionDigits: o.maximumFractionDigits ?? 2,
    minimumFractionDigits: o.minimumFractionDigits ?? 0,
    useGrouping: o.useGrouping ?? true,
  };
}

/** Format a single number for the active locale. */
export function formatNumber(value: number, opts: NumberFormatOptions = {}): string {
  const { locale, numberingSystem } = resolveLocale(ambient.language, opts.numerals ?? ambient.numerals);
  return numberFormatter(locale, intlNumberOptions(opts, numberingSystem)).format(value);
}

/**
 * Money — an amount WITH its currency (the money concept: the platform never
 * converts, a total is per currency): the active locale's digits and grouping,
 * the currency's own minor units, the CODE beside the figure — never a symbol,
 * symbols collide across a multi-currency register — laid out as `Intl` does
 * for the language ("USD 1,234.50" · "1 234,50 USD" · the Arabic digits).
 */
export function formatMoney(amount: number, currency: string, opts: { numerals?: Numerals } = {}): string {
  const { locale, numberingSystem } = resolveLocale(ambient.language, opts.numerals ?? ambient.numerals);
  return numberFormatter(locale, { numberingSystem, style: 'currency', currency, currencyDisplay: 'code' }).format(amount);
}

/** A currency's name in the viewer's language (`Intl.DisplayNames` — CLDR ships every language, air-gapped); the code
 *  itself when the runtime does not know it. */
export function currencyName(code: string, language: string = ambient.language): string {
  try {
    return new Intl.DisplayNames([language], { type: 'currency' }).of(code) ?? code;
  } catch {
    return code;
  }
}

/** ISO alpha-2 → the country's name in the viewer's language (`Intl.DisplayNames` — no stored name column; ADR-030);
 *  the code itself when the runtime does not know it. */
export function countryName(iso: string, language: string = ambient.language): string {
  try {
    return new Intl.DisplayNames([language === 'ar' ? 'ar' : language === 'fr' ? 'fr' : 'en'], { type: 'region' }).of(iso) ?? iso;
  } catch {
    return iso;
  }
}

/** IANA zone → "Asia/Riyadh (UTC+03:00)" — the current UTC offset appended so the person sees exactly what a zone
 *  means. Null/empty → a dash. (The offset is "now" — a DST zone shows its current one, the useful cue at pick time.) */
export function timezoneLabel(iana: string | null | undefined): string {
  if (!iana) return '—';
  try {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: iana, timeZoneName: 'longOffset' }).formatToParts(new Date());
    const off = parts.find((p) => p.type === 'timeZoneName')?.value; // e.g. "GMT+03:00"
    return off ? `${iana} (${off.replace('GMT', 'UTC')})` : iana;
  } catch {
    return iana;
  }
}

/** A zone's NAME in a language ("Arabian Standard Time" · « heure d’Arabie » · «التوقيت العربي») — `Intl`'s generic
 *  name (CLDR ships every language, air-gapped); the IANA id itself when the runtime has no words for it (an offset
 *  like "GMT+03:00" or «غرينتش+٠٣:٠٠» is no name). UTC is said as "UTC" in every language (ADR-082 §2). The drift
 *  strip, the peek and the export dialog say a zone by it — a city id is not a translation, and no city list exists.
 *  Several zones share one name (Riyadh and Kuwait: one clock); a caller that must tell two apart adds the city. */
export function zoneName(zone: string, language: string = ambient.language): string {
  if (zone === 'UTC' || zone === 'Etc/UTC') return 'UTC';
  const locale = LOCALE_BY_LANGUAGE[language as Language] ?? LOCALE_BY_LANGUAGE.en;
  try {
    const name = new Intl.DateTimeFormat(locale, { timeZone: zone, timeZoneName: 'longGeneric' }).formatToParts(new Date()).find((p) => p.type === 'timeZoneName')?.value;
    return name && !OFFSET_NAME_RE.test(name) ? name : zone;
  } catch {
    return zone;
  }
}
// An offset posing as a name, in Latin or Arabic-Indic digits, with or without CLDR's GMT word.
const OFFSET_NAME_RE = /^(?:GMT|UTC|غرينتش)?\s*[+\-−][0-9\u0660-\u0669\u06F0-\u06F9]/;

/** The zone's offset right now ("+03:00") — to tell two zones apart by the clock they read today. Null when unknown. */
export function zoneOffsetNow(zone: string): string | null {
  try {
    const off = new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: 'longOffset' }).formatToParts(new Date()).find((p) => p.type === 'timeZoneName')?.value;
    return off === undefined ? null : off.replace('GMT', '') || '+00:00';
  } catch {
    return null;
  }
}

/** The difference between two zones' clocks right now, in MINUTES (`zone` minus `other` — positive = `zone` is ahead):
 *  what a caption says beside a zone's name ("2 h ahead of you" — ADR-082 §2). Null when either is unknown. */
export function zoneDifferenceNow(zone: string, other: string): number | null {
  const minutes = (off: string | null): number | null => {
    const m = off === null ? null : /^([+\-−])(\d{2}):(\d{2})$/.exec(off);
    return m ? (m[1] === '+' ? 1 : -1) * (Number(m[2]) * 60 + Number(m[3])) : null;
  };
  const a = minutes(zoneOffsetNow(zone));
  const b = minutes(zoneOffsetNow(other));
  return a === null || b === null ? null : a - b;
}

const knownZones = new Map<string, string>();
/** The zone itself when this runtime can format in it, else UTC — a zone the server accepted but an older browser's
 *  ICU has no rules for must not throw on every row it reaches (the hub reads each visit in its site's zone). Judged
 *  once per id. */
export function knownZone(zone: string | undefined): string | undefined {
  if (zone === undefined) return undefined;
  let known = knownZones.get(zone);
  if (known === undefined) {
    try {
      new Intl.DateTimeFormat('en', { timeZone: zone });
      known = zone;
    } catch {
      known = 'UTC';
    }
    knownZones.set(zone, known);
  }
  return known;
}

/** Names joined the active language's way ("Ground Mount and Microgrid" · « … et … » · "… و…") — `Intl.ListFormat`. */
export function formatList(items: readonly string[]): string {
  return new Intl.ListFormat(LOCALE_BY_LANGUAGE[ambient.language], { type: 'conjunction' }).format(items);
}

/**
 * Format a min–max numeric range in one locale-correct string (separator +
 * bidi handled by Intl.formatRange). Both ends share the same options — the
 * caller (formatValue) scales both to one unit first (pin-by-max).
 */
export function formatNumberRange(min: number, max: number, opts: NumberFormatOptions = {}): string {
  const { locale, numberingSystem } = resolveLocale(ambient.language, opts.numerals ?? ambient.numerals);
  return numberFormatter(locale, intlNumberOptions(opts, numberingSystem)).formatRange(min, max);
}

// ── date formatting ──────────────────────────────────────────────────────

// Presets keep call sites declarative ("show a date" vs "a date+time") instead
// of every caller hand-assembling Intl options. calendar:'gregory' is forced on
// all of them (ADR-030 — no Hijri).
export type DatePreset = 'date' | 'datetime' | 'dateLong' | 'time' | 'dayMonth' | 'dayMonthYear' | 'monthYear' | 'year' | 'weekdayDayMonth' | 'weekdayDayMonthTime';

const DATE_PRESETS: Record<DatePreset, Intl.DateTimeFormatOptions> = {
  date: { dateStyle: 'short' },
  datetime: { dateStyle: 'short', timeStyle: 'short' },
  dateLong: { dateStyle: 'long' },
  time: { timeStyle: 'short' },
  // A caption's date: the day and the month (the year only when it is not this year — the caller picks).
  dayMonth: { day: 'numeric', month: 'short' },
  dayMonthYear: { day: 'numeric', month: 'short', year: 'numeric' },
  // A divider's month and year (the files flow cut by month · by year — a calendar date, the same for every viewer).
  monthYear: { month: 'long', year: 'numeric' },
  year: { year: 'numeric' },
  // A period's edge ("since Sat 4 Oct" — ADR-082 §4): the weekday tells the reader which day a word starts on.
  weekdayDayMonth: { weekday: 'short', day: 'numeric', month: 'short' },
  weekdayDayMonthTime: { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' },
};
// A calendar day has no clock: the time-bearing presets fall back to their date half.
const DAY_PRESETS: Record<DatePreset, Intl.DateTimeFormatOptions> = {
  ...DATE_PRESETS,
  datetime: DATE_PRESETS.date,
  time: DATE_PRESETS.date,
  weekdayDayMonthTime: DATE_PRESETS.weekdayDayMonth,
};

/**
 * Format a date/timestamp for the active locale. Accepts a Date, an ISO string,
 * or epoch ms. Returns null for null/undefined/invalid input (caller renders —).
 */
export function formatDate(
  value: Date | string | number | null | undefined,
  preset: DatePreset = 'date',
  opts: { numerals?: Numerals; timeZone?: string } = {},
): string | null {
  if (value === null || value === undefined) return null;
  // A CALENDAR DAY (`YYYY-MM-DD` — every `_on` column, every `effective_from`)
  // has no clock and no zone: it is pinned at UTC midnight and formatted IN UTC,
  // so it reads as that day for every viewer. Routing it through `new Date(str)`
  // + the browser zone shifted it a day back for anyone west of UTC.
  const day = typeof value === 'string' ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(value) : null;
  const date = day ? new Date(Date.UTC(Number(day[1]), Number(day[2]) - 1, Number(day[3]))) : value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  // An impossible day (`2026-02-31`) must not roll over into March — it is not a day.
  if (day && date.toISOString().slice(0, 10) !== value) return null;

  const { locale, numberingSystem } = resolveLocale(ambient.language, opts.numerals ?? ambient.numerals);
  return dateFormatter(locale, {
    ...(day ? DAY_PRESETS[preset] : DATE_PRESETS[preset]),
    calendar: 'gregory',
    numberingSystem,
    // The display zone — an IANA name (ADR-030). Stored UTC, converted at render;
    // omitted → the runtime's local zone (browser). The caller resolves WHICH zone
    // (site for field data, user for account) ONCE and passes the name here. A
    // calendar day ignores it — a day is the same day everywhere.
    ...(day ? { timeZone: 'UTC' } : opts.timeZone ? { timeZone: knownZone(opts.timeZone) } : {}),
  }).format(date);
}

/** Two CALENDAR DAYS as one range the locale's way — "1–7 Oct" · "28 Sep – 4 Oct" · "1 Jan – 31 Dec 2025"
 *  (`Intl`'s own `formatRange`: the dash, the order and the shared parts are its — never joined by hand, ADR-082 §4);
 *  one day reads as that day ("7 Oct"). Days have no zone (pinned at UTC midnight, formatted in UTC, like `formatDate`);
 *  `year` adds the year — a caller sets it when the days are not in this year. Empty for an unreadable day. */
export function formatDayRange(first: string, last: string, opts: { year?: boolean; numerals?: Numerals } = {}): string {
  const dayOf = (day: string): Date | null => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
    if (!m) return null;
    const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
    return d.toISOString().slice(0, 10) === day ? d : null;
  };
  const a = dayOf(first);
  const b = dayOf(last);
  if (!a || !b) return '';
  const { locale, numberingSystem } = resolveLocale(ambient.language, opts.numerals ?? ambient.numerals);
  const fmt = dateFormatter(locale, { day: 'numeric', month: 'short', ...(opts.year ? { year: 'numeric' } : {}), calendar: 'gregory', numberingSystem, timeZone: 'UTC' });
  return first === last ? fmt.format(a) : fmt.formatRange(a, b);
}

/** A clock range in ONE zone — "13:05–14:40" the locale's way (`Intl`'s own separator and bidi handling; ADR-082 §4 —
 *  never two clocks joined by hand). Two clocks, never two dates: a range across midnight (an evening visit) reads
 *  "23:30–01:10" — the day is the row's — so the second clock is handed over ON THE FIRST CLOCK'S DAY, where a
 *  cross-day pair would make `formatRange` spell both dates out. Empty for an unreadable end. */
export function formatTimeRange(fromIso: string, toIso: string, opts: { timeZone?: string; numerals?: Numerals } = {}): string {
  const from = new Date(fromIso);
  const to = new Date(toIso);
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return '';
  const { locale, numberingSystem } = resolveLocale(ambient.language, opts.numerals ?? ambient.numerals);
  const timeZone = knownZone(opts.timeZone);
  return dateFormatter(locale, { timeStyle: 'short', calendar: 'gregory', numberingSystem, ...(timeZone ? { timeZone } : {}) }).formatRange(from, onDayOf(from, to, timeZone));
}

/** `to`'s wall clock on `from`'s calendar day in the zone — `to` itself when they share the day. */
function onDayOf(from: Date, to: Date, timeZone: string | undefined): Date {
  const wall = (d: Date) => {
    const parts = new Intl.DateTimeFormat('en-CA', { ...(timeZone ? { timeZone } : {}), hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }).formatToParts(d);
    const n = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? 0);
    return { day: Date.UTC(n('year'), n('month') - 1, n('day')), clock: n('hour') * 60 + n('minute') };
  };
  const a = wall(from);
  const b = wall(to);
  if (a.day === b.day) return to;
  // Whole days back, then the minutes a daylight change between the two days would have moved the clock.
  const shifted = new Date(to.getTime() - (b.day - a.day));
  const drift = wall(shifted).clock - b.clock;
  return drift === 0 ? shifted : new Date(shifted.getTime() - drift * 60_000);
}

// ── bytes ───────────────────────────────────────────────────────────────────
// A file size for a caption or a cap hint: the largest binary unit that keeps
// the number readable, through the locale-aware formatNumber (Arabic numerals
// follow the user's preference like every other figure — ADR-030).
const BYTE_UNITS = ['B', 'KB', 'MB', 'GB', 'TB'] as const;
// ── coarse durations (the TIME-column grain) ────────────────────────────────

// Single-unit humanized span — the grain field people actually speak: under an
// hour → minutes, under 48 h → hours, else days. One unit, never compounds
// (the Visit cell's "1 h 35 min" is a different, per-visit precision). Unit
// words follow the ambient language (or an explicit `language` override — a
// compare surface or a server-rendered email names its own).
const SPAN_UNITS: Record<Language, { d: string; h: string; m: string }> = {
  en: { d: 'd', h: 'h', m: 'min' },
  fr: { d: 'j', h: 'h', m: 'min' },
  ar: { d: 'ي', h: 'س', m: 'د' },
};

export function formatDurationCoarse(ms: number, opts: { language?: Language } = {}): string {
  const units = SPAN_UNITS[opts.language ?? ambient.language];
  const mins = Math.max(1, Math.round(Math.abs(ms) / 60_000));
  if (mins < 60) return `${formatNumber(mins)} ${units.m}`;
  const hours = Math.round(mins / 60);
  if (hours < 48) return `${formatNumber(hours)} ${units.h}`;
  return `${formatNumber(Math.round(hours / 24))} ${units.d}`;
}

/** The FINE duration — hours and minutes together ("1 h 35 min"): the grain
 *  ONE visit's ceremony is read at (check-in → check-out on a Visit cell);
 *  under an hour the minutes alone, a whole number of hours drops the minutes.
 *  The coarse single-unit span above stays the Time column's grain. */
export function formatDurationFine(ms: number, opts: { language?: Language } = {}): string {
  const units = SPAN_UNITS[opts.language ?? ambient.language];
  const mins = Math.max(1, Math.round(Math.abs(ms) / 60_000));
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h === 0) return `${formatNumber(m)} ${units.m}`;
  if (m === 0) return `${formatNumber(h)} ${units.h}`;
  return `${formatNumber(h)} ${units.h} ${formatNumber(m)} ${units.m}`;
}

export function formatBytes(bytes: number, opts: { numerals?: Numerals } = {}): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '';
  let value = bytes;
  let i = 0;
  while (value >= 1024 && i < BYTE_UNITS.length - 1) {
    value /= 1024;
    i++;
  }
  const digits = i === 0 ? 0 : value < 10 ? 1 : 0;
  return `${formatNumber(value, { maximumFractionDigits: digits, ...(opts.numerals ? { numerals: opts.numerals } : {}) })} ${BYTE_UNITS[i]}`;
}
